<template>
  <loading-element v-if="authStore.isLoading" />

  <div class="main bg-primary" v-else>
    <div class="headerContainer mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Ajouter"
        secondElement="un ingrédient"
        :coloredSecond="true"
        actionHeaderIcon="mdi-arrow-left"
        :actionheaderFunction="() => $router.push('/my_ingredients')"
        actionHeaderColor="accent"
        actionHeaderTextColor="white"
      />
    </div>

    <v-card v-if="productInfo.foodInfo" class="pa-4 mb-8" elevation="4">
      <v-row>
        <v-col cols="12" md="3" class="ingredientImageContainer">
          <v-img
            :src="productInfo.productImage"
            width="100%"
            class="rounded-lg"
            contain
          />
        </v-col>

        <v-col cols="12" md="8">
          <h2 class="text-h5 font-weight-medium mb-2">
            {{ productInfo.productName }}
          </h2>
          <p class="text-subtitle-1 mb-2">{{ productInfo.productBrand }}</p>

          <v-chip
            v-if="productInfo.novaGroup.string"
            :color="productInfo.novaGroup.color"
            class="ma-1"
            label
            small
            outlined
          >
            {{ $t(productInfo.novaGroup.string) }}
          </v-chip>

          <div class="mt-1">
            <v-chip
              v-for="(label, index) in productInfo.labels"
              :key="'label-' + index"
              class="ma-1"
              label
              small
            >
              {{ label }}
            </v-chip>
          </div>

          <div class="mt-2">
            <v-chip
              v-for="(nutrient, index) in productInfo.nutriments_map"
              :key="'nutrient-' + index"
              class="ma-1"
              color="blue"
              text-color="white"
              label
              small
            >
              {{ $t(nutrient.key) }} : {{ nutrient.value }}
            </v-chip>
          </div>

          <div class="mt-4">
            <h3 class="text-subtitle-1 font-weight-medium">Allergènes :</h3>
            <v-chip
              v-for="(allergen, index) in productInfo.allergens"
              :key="'allergen-' + index"
              color="orange"
              class="ma-1"
              label
              small
            >
              {{ $t(allergen) }}
            </v-chip>
          </div>

          <div class="mt-4">
            <h3 class="text-subtitle-1 font-weight-medium">Additifs :</h3>
            <v-chip
              v-for="(additive, index) in productInfo.additives"
              :key="'additive-' + index"
              color="red"
              class="ma-1"
              label
              small
            >
              {{ additive }}
            </v-chip>
          </div>

          <div class="mt-4">
            <p v-if="productInfo.servingQuantity">
              Portion : {{ productInfo.servingQuantity }}g
            </p>
            <p v-if="productInfo.servingSize">
              Taille de portion : {{ productInfo.servingSize }}
            </p>
          </div>
        </v-col>
      </v-row>

      <v-divider class="mt-4 mb-3" />
      <SeasonalityPicker
        v-model="seasonMonths"
        :frozen="isFrozen"
        @update:frozen="isFrozen = $event"
      />

      <v-btn color="accent" class="mt-4" @click="saveToCollection">
        Ajouter l'ingrédient
      </v-btn>
    </v-card>

    <!-- ====== NOUVEAU : RECHERCHE PAR NOM (composant) ====== -->
    <div v-if="!items.length" class="mb-8 searchSection">
        <IngredientSearch @ingredient-added="onIngredientAdded" />
    </div>

    <!-- ====== NOUVEAU : IMPORT JSON DE L'EXTENSION + CARTES MULTIPLES ====== -->
    <div class="d-flex align-center mb-4 importBar">
      <v-btn
        color="primary"
        rounded="lg"
        class="white-text-button mr-3"
        @click="importFromExtension"
        :loading="isImporting"
      >
        <v-icon class="mr-1">mdi-clipboard-arrow-down</v-icon>
        Récupérer les aliments de l'extension
      </v-btn>

      <v-spacer />

      <v-btn
        color="error"
        variant="tonal"
        class="clearListButton"
        @click="resetList"
        :enabled="items.length > 0 && !isImporting"
      >
        <v-icon class="mr-1">mdi-broom</v-icon>
        Vider la liste
      </v-btn>

      <v-btn
        color="accent"
        rounded="lg"
        class="ml-2"
        @click="addAllToCollection"
        :disabled="isPushing || itemsFoundCount === 0 || !allItemsValid"
        :loading="isPushing"
      >
        <v-icon class="mr-1" >mdi-cloud-upload</v-icon>
        Ajouter tous ({{ itemsFoundCount }})
      </v-btn>
    </div>

    <!-- Récap entrées invalides -->
    <v-alert
      v-if="itemsNotFoundCount > 0"
      type="warning"
      variant="tonal"
      class="mb-6"
      title="Entrées non valides"
    >
      Certaines entrées du JSON ne contiennent pas les données minimales.
      <div class="mt-2">
        <v-chip
          v-for="(bc, i) in itemsNotFoundBarcodes"
          :key="bc + '-' + i"
          class="ma-1"
          color="warning"
          label
          small
        >
          {{ bc }}
        </v-chip>
      </div>
      <div class="mt-2">
        <v-btn
          size="small"
          variant="text"
          color="warning"
          @click="removeNotFound"
        >
          <v-icon size="18" class="mr-1">mdi-close-circle</v-icon>
          Retirer ces entrées
        </v-btn>
      </div>
    </v-alert>

    <!-- Alerte pour champs manquants -->
    <v-alert
      v-if="!allItemsValid && itemsFoundCount > 0"
      type="error"
      variant="tonal"
      class="mb-6"
      title="Champs obligatoires manquants"
    >
      Certains produits n'ont pas tous les champs obligatoires remplis (nom, marque, portion, taille de portion).
    </v-alert>

    <!-- Cartes ajout par extension -->
    <v-row v-if="items.length">
      <v-col
        v-for="(it, idx) in items"
        :key="it.barcode + '-' + idx"
        cols="12"
        md="6"
        lg="4"
      >
        <ExtensionResultCard 
          :item="it" 
          @remove="removeItem(idx)" 
          @update:item="updateItem(idx, $event)"
        />
      </v-col>
    </v-row>
  </div>

  <v-snackbar
    v-model="snackbar"
    :color="snackbarColor"
    top
    right
    timeout="3500"
  >
    {{ errorMessage || snackbarMessage }}
  </v-snackbar>
</template>

<script>
import { ref, computed } from "vue";
import {
  getNovaStringAndColor,
  addIngredientToUserCollection,
  addIngredientsBulkToUserCollection,
} from "@/service/ingredient_service";
import { getFoodInfoWithBarcode } from "@/assets/js/gameSearchJs";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import IngredientSearch from "@/components/Kitchen/Ingredients/IngredientSearch.vue";
import ExtensionResultCard from "@/components/Kitchen/Ingredients/ExtensionResultCard.vue";
import { checkProductWithIntolerances } from "@/service/intolerance_service";
import SeasonalityPicker from "@/components/Kitchen/Ingredients/SeasonalityPicker.vue";

export default {
  name: "AddIngredientPage",
  components: { LoadingElement, TopDeck, IngredientSearch, ExtensionResultCard, SeasonalityPicker },
  setup() {
    const authStore = useAuthStore();

    // ====== état existant
    const snackbar = ref(false);
    const errorMessage = ref("");
    const productInfo = ref({
      barcode: "8000500110683",
      foodInfo: null,
      productName: "",
      productImage: "",
      productBrand: "",
      nutriscore: "",
      novaGroup: { string: "", color: "" },
      labels: [],
      nutriments_map: [],
      servingQuantity: null,
      servingSize: null,
      allergens: [],
      additives: [],
    });

    // ====== nouvel état pour les cartes multiples
    const items = ref([]); // [{status:'found'|'not_found', barcode, ...}]
    const isImporting = ref(false);
    const isPushing = ref(false);
    const snackbarMessage = ref("");
    const snackbarColor = ref("success");

    // Saisonnalité pour le flux code-barres individuel
    const seasonMonths = ref([]);
    const isFrozen = ref(false);

    const itemsFoundCount = computed(
      () => items.value.filter((i) => i.status === "found").length
    );
    const itemsNotFoundCount = computed(
      () => items.value.filter((i) => i.status === "not_found").length
    );
    const itemsNotFoundBarcodes = computed(() =>
      items.value
        .filter((i) => i.status === "not_found")
        .map((i) => i.barcode || "—")
    );

    // Validation : tous les items "found" doivent avoir les champs obligatoires
    const allItemsValid = computed(() => {
      const foundItems = items.value.filter((i) => i.status === "found");
      if (foundItems.length === 0) return false;
      
      return foundItems.every((item) => {
        return (
          item.productName &&
          item.productBrand &&
          item.servingQuantity &&
          item.servingSize
        );
      });
    });

    const showSnack = (msg, color = "success") => {
      snackbarMessage.value = msg;
      snackbarColor.value = color;
      snackbar.value = true;
    };

    // ====== helpers communs
    const extractNutrimentsFromJson = (nutriments) => {
      const nutrimentsArray = [];
      const importantKeys = [
        "energy-kcal",
        "fat",
        "saturated-fat",
        "sugars",
        "salt",
        "proteins",
        "carbohydrates",
        "fiber",
      ];
      for (const key in nutriments || {}) {
        if (key.endsWith("_100g")) {
          const name = key.replace("_100g", "");
          if (!importantKeys.includes(name)) continue;
          const value = nutriments[key];
          const unit = nutriments[`${name}_unit`] || "g";
          nutrimentsArray.push({ key: name, value: `${value} ${unit}` });
        }
      }
      return nutrimentsArray;
    };

    const cleanInfos = () => {
      productInfo.value.foodInfo = null;
      productInfo.value.productName = "";
      productInfo.value.productImage = "";
      productInfo.value.productBrand = "";
      productInfo.value.nutriscore = "";
      productInfo.value.novaGroup = { string: "", color: "" };
      productInfo.value.labels = [];
      productInfo.value.nutriments_map = [];
      productInfo.value.servingQuantity = null;
      productInfo.value.servingSize = null;
      productInfo.value.allergens = [];
      productInfo.value.additives = [];
    };

    const getNutriscoreGrade = (source) => {
      if (!source) return "";
      const nutriscore = source.nutriscore;
      let grade = "";

      if (Array.isArray(nutriscore)) {
        const entry = nutriscore.find(
          (item) => item && typeof item === "object" && item.grade
        );
        grade = entry?.grade || "";
      } else if (nutriscore && typeof nutriscore === "object") {
        grade = nutriscore.grade || "";
      }

      if (!grade && source.nutriscore_grade) grade = source.nutriscore_grade;
      if (!grade && source.nutriscore_data?.grade) {
        grade = source.nutriscore_data.grade;
      }

      return grade || "";
    };

    // ====== flux existant : recherche par code-barres (conservé)
    const searchWithBarcode = () => {
      if (!productInfo.value.barcode) {
        errorMessage.value = "Veuillez entrer un code-barres valide.";
        snackbar.value = true;
        return;
      }
      cleanInfos();
      getFoodInfoWithBarcode(productInfo.value.barcode).then((product) => {
        if (product) {
          productInfo.value.foodInfo = product;
          productInfo.value.productImage =
            product.image_front_url || "https://via.placeholder.com/150";
          productInfo.value.productBrand =
            product.brands || "Marque non disponible";
          productInfo.value.productName =
            product.product_name_fr || "Nom non disponible";
          productInfo.value.nutriscore = getNutriscoreGrade(product);
          productInfo.value.novaGroup = getNovaStringAndColor(
            product.nova_group
          );
          productInfo.value.labels = product.labels ? [product.labels] : [];
          productInfo.value.nutriments_map = extractNutrimentsFromJson(
            product.nutriments || {}
          );
          productInfo.value.servingQuantity = product.serving_quantity || null;
          productInfo.value.servingSize = product.serving_size || null;
          productInfo.value.allergens = (product.allergens_tags || []).map(
            (tag) => tag.split(":")[1]
          );
          productInfo.value.additives = (product.additives_tags || []).map(
            (tag) => tag.split(":")[1]
          );
          const userConstraints = [
            ...(Array.isArray(authStore.userInfos?.intolerances)
              ? authStore.userInfos.intolerances
              : []),
            ...(Array.isArray(authStore.userInfos?.allergies)
              ? authStore.userInfos.allergies
              : []),
          ];
          const res = checkProductWithIntolerances(product, userConstraints);

          console.debug("Résultat checkProductWithIntolerances:", res);
        }
      });
    };

    const saveToCollection = () => {
      if (!productInfo.value.foodInfo) {
        errorMessage.value = "Aucune information sur l'ingrédient à ajouter.";
        snackbar.value = true;
        return;
      }

      const ingredientId = productInfo.value.barcode;

      const ingredientData = {
        productName: productInfo.value.productName,
        productImage: productInfo.value.productImage,
        productBrand: productInfo.value.productBrand,
        nutriscore: productInfo.value.nutriscore,
        novaGroup: productInfo.value.novaGroup,
        labels: productInfo.value.labels,
        nutriments_map: productInfo.value.nutriments_map,
        servingQuantity: productInfo.value.servingQuantity,
        servingSize: productInfo.value.servingSize,
        allergens: productInfo.value.allergens,
        additives: productInfo.value.additives,
        favorite: false,
        userId: authStore.userid,
        added_dt: new Date().toISOString(),
        category: "",
        seasonMonths: [...seasonMonths.value],
        isFrozen: isFrozen.value,
      };

      addIngredientToUserCollection(
        authStore.userid,
        ingredientId,
        ingredientData
      )
        .then((res) => {
          if (res.alreadyExists) {
            errorMessage.value =
              "L'ingrédient est déjà présent dans votre liste.";
            snackbar.value = true;
          } else {
            errorMessage.value = "Ingrédient ajouté avec succès !";
            cleanInfos();
            snackbar.value = true;
          }
        })
        .catch((error) => {
          console.error("Erreur lors de l'ajout de l'ingrédient :", error);
          errorMessage.value = "Erreur lors de l'ajout de l'ingrédient.";
          snackbar.value = true;
        });
    };

    // Callback quand un ingrédient est ajouté depuis la recherche
    const onIngredientAdded = (ingredient) => {
      console.debug("Ingrédient ajouté depuis la recherche :", ingredient);
    };

    // ====== NOUVEAU : import JSON depuis l'extension
    const toItemFromSlim = (slim) => {
      if (!slim || !slim.barcode) {
        return { barcode: slim?.barcode ?? "—", status: "not_found" };
      }
      return {
        barcode: slim.barcode,
        status: "found",
        foodInfo: slim,
        productImage: slim.productImage || "https://via.placeholder.com/150",
        productBrand: slim.productBrand || "Marque non disponible",
        productName: slim.productName || "Nom non disponible",
        nutriscore: getNutriscoreGrade(slim),
        novaGroup: getNovaStringAndColor(slim.nova_group),
        labels: slim.labels || [],
        nutriments_map: extractNutrimentsFromJson(slim.nutriments || {}),
        servingQuantity: slim.serving_quantity || null,
        servingSize: slim.serving_size || null,
        allergens: (slim.allergens_tags || []).map((t) =>
          typeof t === "string" && t.includes(":") ? t.split(":")[1] : t
        ),
        additives: (slim.additives_tags || []).map((t) =>
          typeof t === "string" && t.includes(":") ? t.split(":")[1] : t
        ),
        favorite: false,
        category: "",
      };
    };

    const importFromExtension = async () => {
      try {
        isImporting.value = true;
        const text = await navigator.clipboard.readText();
        if (!text) {
          showSnack("Presse-papiers vide.", "warning");
          return;
        }

        let payload = null;
        try {
          payload = JSON.parse(text);
        } catch {
          showSnack(
            "Le presse-papiers ne contient pas un JSON valide.",
            "error"
          );
          return;
        }

        if (
          !payload ||
          payload.source !== "off-extension" ||
          !Array.isArray(payload.items)
        ) {
          showSnack("Format JSON inattendu (source inconnue).", "error");
          return;
        }

        const mapped = payload.items.map(toItemFromSlim);

        // dédup par barcode (le dernier l'emporte)
        const byBc = new Map();
        for (const it of mapped) byBc.set(it.barcode, it);
        items.value = Array.from(byBc.values());

        const found = itemsFoundCount.value;
        const miss = items.value.length - found;
        if (found) showSnack(`${found} produit(s) importé(s).`, "success");
        if (miss) showSnack(`${miss} entrée(s) non valide(s).`, "warning");
      } catch (e) {
        console.error(e);
        showSnack("Erreur pendant l'import.", "error");
      } finally {
        isImporting.value = false;
      }
    };

    // ====== gestion cartes multiples
    const removeItem = (index) => {
      items.value.splice(index, 1);
    };
    const updateItem = (index, updatedItem) => {
      // Ne met à jour que si la valeur change réellement pour éviter les updates récursifs
      const current = items.value[index];
      let changed = false;
      for (const key in updatedItem) {
        if (updatedItem[key] !== current[key]) {
          changed = true;
          break;
        }
      }
      if (changed) {
        items.value[index] = { ...current, ...updatedItem };
      }
    };
    const removeNotFound = () => {
      items.value = items.value.filter((i) => i.status === "found");
    };
    const resetList = () => {
      items.value = [];
    };

    const addAllToCollection = async () => {
      if (!authStore?.userid) {
        showSnack("Utilisateur non authentifié.", "error");
        return;
      }
      const foundItems = items.value.filter((i) => i.status === "found");
      if (!foundItems.length) {
        showSnack("Aucun produit à ajouter.", "warning");
        return;
      }

      isPushing.value = true;
      try {
        // Construire la map { [barcode]: ingredientData }
        const payload = {};
        for (const it of foundItems) {
          payload[it.barcode] = {
            productName: it.productName,
            productImage: it.productImage,
            productBrand: it.productBrand,
            nutriscore: it.nutriscore || "",
            novaGroup: it.novaGroup,
            labels: it.labels,
            nutriments_map: it.nutriments_map,
            servingQuantity: it.servingQuantity,
            servingSize: it.servingSize,
            allergens: it.allergens,
            additives: it.additives,
            favorite: false,
            userId: authStore.userid,
            added_dt: new Date().toISOString(),
            category: it.category || "",
          };
        }

        const { added, already } = await addIngredientsBulkToUserCollection(
          authStore.userid,
          payload
        );

        if (added > 0) showSnack(`${added} ajouté(s) avec succès.`, "success");
        if (already > 0) showSnack(`${already} déjà présent(s).`, "info");
        if (added === 0 && already === 0) showSnack("Rien à ajouter.", "info");
      } catch (e) {
        console.error(e);
        showSnack("Erreur lors de l'ajout en lot.", "error");
      } finally {
        isPushing.value = false;
      }
    };

    return {
      authStore,
      // existant
      productInfo,
      errorMessage,
      snackbar,
      searchWithBarcode,
      saveToCollection,
      cleanInfos,
      onIngredientAdded,
      // saisonnalité flux individuel
      seasonMonths,
      isFrozen,
      // nouveau
      items,
      isImporting,
      isPushing,
      importFromExtension,
      removeItem,
      updateItem,
      removeNotFound,
      resetList,
      addAllToCollection,
      itemsFoundCount,
      itemsNotFoundCount,
      itemsNotFoundBarcodes,
      snackbarMessage,
      snackbarColor,
      allItemsValid,
    };
  },
};
</script>

<style scoped>
.main {
  padding: 28px 24px 48px;
}

.headerContainer {
  margin-bottom: 24px;
}

.searchSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.importBar {
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(var(--v-theme-text), 0.1);
  background: rgba(var(--v-theme-secondary), 0.75);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.16);
}

.importBar :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0.2px;
}

.clearListButton {
  font-weight: 600;
  border: 1px solid rgba(var(--v-theme-error), 0.45);
}

.ingredientImageContainer {
  display: flex;
  justify-content: center;
  align-items: center;
}
.opacity-70 {
  opacity: 0.7;
}
</style>
